// ── Design decisions ──
// Panel lateral ancho ('xl') con la fuente de la lista a la izquierda (SourcePane,
// fijo) y los grupos extraídos a la derecha, para que el operador compare fila
// por fila sin cambiar de pantalla. Todo lo que se ve acá es un BORRADOR local:
// nada llega al viaje hasta "Confirmar y agregar"; Descartar tira el estado.
//
// · El cliente NUNCA se preselecciona, ni con una sola coincidencia: el fuzzy
//   match solo reordena la lista del ClientePicker (y si no hay candidatos se
//   ofrece la lista completa). "Maria L." puede ser MLR o MLH y eso lo decide
//   una persona. El nombre leído es editable (lápiz) para corregir garabatos
//   tipo "Rosse Timmo Izagirre" ANTES de que el fuzzy match corra sobre él.
// · unit_price null ⇒ FOB 0 + requires_review true + pista "Sin precio en la
//   lista". Así nada entra al anexo con precio inventado.
// · Catálogo por fila (CatalogSugerencias, justo bajo la descripción): chips de
//   interpretación al instante y top-3 del catálogo 350 ms después de que la
//   descripción se asienta. Pasar el mouse por un candidato previsualiza su CIP
//   en el campo de precio (solo lectura, no escribe). Tocar una fila liga
//   customs_catalog_id, llena el precio desde fob_usd (cae a cip_usd si falta)
//   y guarda cip_unitario_usd; el precio sigue siendo editable. Nunca se elige
//   sola.
// · "+ Agregar artículo" (por paquete) y "+ Agregar cliente" (al pie) crean
//   filas/grupos con extraction_source 'manual' (chip Manual, no IA) para lo
//   que la foto no cubrió.
// · Cierre SIN confirmar ni descartar (Esc / ✕ / fondo) ⇒ onGuardarBorrador
//   con la foto del estado, para retomar después sin gastar tokens. Confirmar
//   y Descartar NO guardan borrador: el padre borra el que hubiera.
// · `borrador` (payload guardado) manda sobre `resultado` al hidratar: es la
//   ruta "Ver última entrada".
// · Confirmar queda deshabilitado hasta que TODOS los grupos tengan cliente,
//   tipo de ingreso y al menos un artículo con descripción; el bulto NO
//   bloquea (se asigna en producción). El error de la API se muestra inline
//   (role="alert") sin cerrar el panel para no perder lo editado.
// · Piel "Ambiance" (ambiance.css, tokens sacados del proyecto de diseño
//   "Nueva Entrada - Ambiance"): papel cálido, tipografía IBM Plex, campos con
//   subrayado en vez de caja y teal como acento. Vive SÓLO acá: el scope
//   `.amb-ext` lo abre el Drawer cuando le pasamos `ambiance`, y los
//   compartidos (ClientePicker, BultoSelect, TipoIngresoControl, IsNewToggle,
//   chips) sólo cambian de piel con ese mismo prop, así que ninguna otra
//   pantalla se entera. Tres excepciones de color deliberadas: los chips
//   ámbar "Sin cliente"/"Sin bulto" quedan como están, el segmentado de tipo
//   de ingreso pinta Pasajero en teal y Carga en el azul corporativo, y el
//   relleno del botón primario no se toca (Ambiance sólo le da radio,
//   tipografía y sombra).

// · Rediseño mobile-first (delta AMB-MOBILE): en el teléfono el visor de la
//   foto va arriba —compacto y colapsable— y la lista fluye a lo ancho debajo,
//   con el pie (totales + CTA) alcanzable con el pulgar; en escritorio queda
//   la división lado a lado de siempre. El aviso de borrador se descarta de un
//   toque (estado por sesión del panel: reabrir lo trae de vuelta) y las notas
//   y el chip de caché colapsan con él. El zoom ya no es estado de acá: vive
//   dentro de SourcePane y se reinicia solo al llegar otra foto.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import Drawer from './shared/Drawer';
import ClientePicker from './shared/ClientePicker';
import { BultoSelect } from './shared/BultoRail';
import TipoIngresoControl from './shared/TipoIngresoControl';
import IsNewToggle from './shared/IsNewToggle';
import CatalogSugerencias from './shared/CatalogSugerencias';
import { AutoAsignadoChip, ProvenanceChip, ReviewDot } from './shared/chips';
import {
  contarTokensNombre,
  fuzzyMatchCustomers,
  fuzzyMatchCustomersScored,
} from './shared/labels';
import { UNIDADES } from '../anexo/AnexoItemRow';
import SourcePane from './SourcePane';
import './ambiance.css';

const MAX_DESC = 60;
export const VERSION_BORRADOR = 1;

// Umbrales DGA por tipo de ingreso (USD por paquete). El IVA es 13% plano y no
// cambia el umbral. Paquetes con tipo NULL no entran al chequeo: ya bloquean
// Confirmar por sí solos.
const UMBRAL_DGA = {
  pasajero_encomienda: 200,
  carga_encomienda: 200,
  compra_linea: 300,
};

// Campos Ambiance: subrayado en vez de caja. La línea sólo aparece al enfocar
// en las filas de artículo (`--ghost`) para que una tabla de 6 ítems no se lea
// como una reja.
const CAMPO_FILA = 'amb-in amb-in--ghost';

// El campo del medio de cada artículo cambia de significado según la unidad:
// con KG/LB es un PESO, con UNIDAD/PAR/JUEGO/DOCENA es un CONTEO. La etiqueta
// se deriva del valor actual de unidad_medida — nunca se fija — así que cambiar
// la unidad la actualiza sola.
// OJO (deuda conocida, fuera del alcance de este cambio): las dos cosas se
// guardan en la MISMA columna `cantidad` de viajero_encomienda_items; no existe
// columna de peso. Acá sólo se rotula lo que ya hay.
const UNIDADES_PESO = ['KG', 'LB'];

const etiquetaCantidad = (unidadMedida) =>
  UNIDADES_PESO.includes(String(unidadMedida ?? '').toUpperCase()) ? 'Peso' : 'Cantidad';

const clonar = (obj) => JSON.parse(JSON.stringify(obj));

// Convierte el resultado de la extracción en grupos editables locales.
// Auto-asignación CONSERVADORA: devuelve un cliente solo cuando (a) cada token
// del nombre leído coincide EXACTO con un token del candidato, (b) el puntaje
// del primero supera estrictamente al del segundo (un empate nunca decide) y
// (c) por (b), un solo candidato ostenta el puntaje máximo. Cualquier duda →
// null y el operador elige como siempre. Nunca crea clientes.
export const autoMatch = (ownerNameRaw, customers) => {
  const q = contarTokensNombre(ownerNameRaw);
  if (q === 0) return null;
  const scored = fuzzyMatchCustomersScored(ownerNameRaw, customers);
  if (scored.length === 0) return null;
  const [top, segundo] = scored;
  if (top.exactTokens !== q) return null;
  if (segundo && segundo.score >= top.score) return null;
  return top.c;
};

export const mapearResultado = (resultado, customers = []) => {
  const paquetes = resultado?.extracted?.paquetes || [];
  return paquetes.map((p, gi) => {
    const matched = autoMatch(p.owner_name_raw || '', customers);
    return {
      key: `g-${gi}`,
      owner_name_raw: p.owner_name_raw || '',
      customer_id: matched ? matched.id : null,
      nombre: matched ? matched.full_name : p.owner_name_raw || '',
      customer_auto: Boolean(matched),
      bulto_id: null,
      tipo_ingreso: null,
      extraction_source: 'ai',
      items: (p.items || []).map((it, ii) => {
        // El idioma de los campos del payload de extracción varía según la
        // fuente: cada acceso lleva su cadena de respaldo bilingüe.
        const precio =
          it.cip_unitario_usd ?? it.fob_unitario_usd ?? it.unit_price ?? it.valor_unitario ?? null;
        const sinPrecio = precio === null || precio === undefined;
        return {
          key: `g-${gi}-i-${ii}`,
          descripcion: String(
            it.descripcion ?? it.description_raw ?? it.description ?? it.name ?? ''
          ).slice(0, MAX_DESC),
          cantidad: Number(it.cantidad ?? it.qty ?? it.quantity ?? 1) || 1,
          unidad_medida: it.unidad_medida ?? it.unit_type ?? 'UNIDAD',
          fob_unitario_usd: sinPrecio ? 0 : Number(precio) || 0,
          is_new: it.is_new ?? true,
          requires_review: Boolean(it.requires_review) || sinPrecio,
          sin_precio: sinPrecio,
          notes: it.notes || null,
          extraction_source: 'ai',
          customs_catalog_id: null,
          catalogo_descripcion: null,
          catalogo_unidad_medida: null,
          cip_unitario_usd: null,
          manual_override: true,
        };
      }),
    };
  });
};

// Grupos guardados en un borrador → grupos locales (misma forma; se clona para
// que editar no toque el objeto del padre). Los defaults van ANTES del spread
// para que un borrador guardado con la forma vieja gane los campos nuevos sin
// pisar los que ya trae.
export const mapearBorrador = (borrador) =>
  Array.isArray(borrador?.grupos)
    ? clonar(borrador.grupos).map((g) => ({
        // Un borrador es estado del operador: acá NUNCA se auto-asigna; el
        // default solo cubre borradores guardados antes de que existiera el flag.
        customer_auto: false,
        ...g,
        items: (g.items || []).map((it) => ({
          catalogo_unidad_medida: null,
          manual_override: !it.customs_catalog_id,
          ...it,
        })),
      }))
    : [];

const nuevoItem = (gKey, n) => ({
  key: `${gKey}-m-${n}`,
  descripcion: '',
  cantidad: 1,
  unidad_medida: 'UNIDAD',
  fob_unitario_usd: 0,
  is_new: true,
  requires_review: false,
  sin_precio: false,
  notes: null,
  extraction_source: 'manual',
  customs_catalog_id: null,
  catalogo_descripcion: null,
  catalogo_unidad_medida: null,
  cip_unitario_usd: null,
  manual_override: true,
});

const nuevoGrupo = (n) => ({
  key: `g-m-${n}`,
  owner_name_raw: '',
  customer_id: null,
  nombre: '',
  customer_auto: false,
  bulto_id: null,
  tipo_ingreso: null,
  extraction_source: 'manual',
  items: [],
});

const ExtractionDrawer = ({
  open,
  onClose,
  resultado,
  borrador = null,
  imageUrl,
  sourceTipo = 'imagen',
  sourceMeta = null,
  cached = false,
  customers = [],
  bultos = [],
  onConfirmar,
  onDescartar,
  onGuardarBorrador,
  cargando = false,
  error = null,
  onQuickCreate,
}) => {
  const [grupos, setGrupos] = useState([]);
  // Aviso "es un borrador": informa, no bloquea. Se descarta de un toque y no
  // vuelve a ocupar espacio hasta la próxima sesión del panel.
  const [avisoVisible, setAvisoVisible] = useState(true);
  const [pendiente, setPendiente] = useState(false);
  const [errorConfirmar, setErrorConfirmar] = useState('');
  const [editandoNombre, setEditandoNombre] = useState(null); // key del grupo
  // Vista previa del precio al pasar el mouse por un candidato del catálogo.
  // Transitorio (solo UI): nunca entra al estado del ítem ni al borrador.
  const [hoveredCandidateId, setHoveredCandidateId] = useState(null); // { itemKey, id, cip_usd } | null
  const contador = useRef(0);
  const gruposRef = useRef(grupos);
  gruposRef.current = grupos;
  // Drawer vuelve a enfocar su panel cada vez que cambia la identidad de
  // onClose: el cierre que le pasamos tiene que ser ESTABLE entre renders (si
  // no, cada tecla en un input devuelve el foco al panel). Por eso lo que
  // necesita leer va en refs y el callback no tiene dependencias.
  const cierreRef = useRef({});
  cierreRef.current = { onClose, onGuardarBorrador, onDescartar, pendiente };

  // La lista de clientes entra por ref: si fuera dependencia del efecto de
  // hidratación, un alta rápida re-mapearía los grupos y pisaría lo editado.
  const customersRef = useRef(customers);
  customersRef.current = customers;

  // Rehidratar el borrador local cada vez que llega un resultado (o un borrador
  // guardado) nuevo. El borrador guardado manda: es la ruta "Ver última entrada".
  useEffect(() => {
    setGrupos(borrador ? mapearBorrador(borrador) : mapearResultado(resultado, customersRef.current));
    setAvisoVisible(true);
    setErrorConfirmar('');
    setEditandoNombre(null);
    setHoveredCandidateId(null);
  }, [resultado, borrador, open]);

  const candidatosPorGrupo = useMemo(
    () =>
      grupos.map((g) => {
        const match = fuzzyMatchCustomers(g.owner_name_raw, customers);
        return match.length > 0 ? match : customers;
      }),
    [grupos, customers]
  );

  const cambiarGrupo = (key, patch) =>
    setGrupos((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));

  const cambiarItem = (gKey, iKey, patch) =>
    setGrupos((prev) =>
      prev.map((g) =>
        g.key === gKey
          ? { ...g, items: g.items.map((it) => (it.key === iKey ? { ...it, ...patch } : it)) }
          : g
      )
    );

  const quitarItem = (gKey, iKey) =>
    setGrupos((prev) =>
      prev.map((g) => (g.key === gKey ? { ...g, items: g.items.filter((it) => it.key !== iKey) } : g))
    );

  const agregarItem = (gKey) => {
    contador.current += 1;
    const n = contador.current;
    setGrupos((prev) =>
      prev.map((g) => (g.key === gKey ? { ...g, items: [...g.items, nuevoItem(gKey, n)] } : g))
    );
  };

  const agregarGrupo = () => {
    contador.current += 1;
    const g = nuevoGrupo(contador.current);
    setGrupos((prev) => [...prev, g]);
    setEditandoNombre(g.key);
  };

  const quitarGrupo = (gKey) => setGrupos((prev) => prev.filter((g) => g.key !== gKey));

  // Nombre leído editable: si todavía no hay cliente elegido, también es el
  // nombre del paquete.
  const confirmarNombre = (g, valor) => {
    const limpio = String(valor ?? '').trim();
    cambiarGrupo(g.key, {
      owner_name_raw: limpio,
      nombre: g.customer_id ? g.nombre : limpio,
      // Editar el nombre leído también es un toque del operador.
      customer_auto: false,
    });
    setEditandoNombre(null);
  };

  const elegirCatalogo = (g, it, row) => {
    const cip = row?.cip_usd === null || row?.cip_usd === undefined ? null : Number(row.cip_usd);
    // La columna de precio "usado" del catálogo está vacía en las 792 filas:
    // el precio sale de fob_usd y, si falta, cae a cip_usd. Nuevo/Usado solo
    // escribe is_new y nunca toca el precio.
    const fob = row?.fob_usd === null || row?.fob_usd === undefined ? cip : Number(row.fob_usd);
    // Al ligar, la lista de candidatos se desmonta y su onMouseLeave ya no
    // dispara: la vista previa se limpia acá.
    setHoveredCandidateId(null);
    cambiarItem(g.key, it.key, {
      customs_catalog_id: row.id,
      catalogo_descripcion: row.descripcion ?? null,
      catalogo_unidad_medida: row.unidad_medida ?? null,
      manual_override: false,
      cip_unitario_usd: cip,
      // El precio se llena desde el catálogo pero sigue siendo editable.
      fob_unitario_usd: fob === null ? it.fob_unitario_usd : fob,
      sin_precio: fob === null ? it.sin_precio : false,
    });
  };

  // Desligar NO toca `descripcion`: el texto crudo extraído vive ahí intacto
  // (la vista ligada muestra catalogo_descripcion sin sobreescribirlo), así
  // que soltar el vínculo lo restaura solo.
  const desligarCatalogo = (g, it) => {
    setHoveredCandidateId(null);
    cambiarItem(g.key, it.key, {
      customs_catalog_id: null,
      catalogo_descripcion: null,
      catalogo_unidad_medida: null,
      cip_unitario_usd: null,
      manual_override: true,
    });
  };

  // ── Compuerta de Confirmar ──────────────────────────────────────────────
  // Cliente y tipo_ingreso son obligatorios por paquete (un tipo NULL produce
  // un Anexo inválido) y cada paquete necesita al menos un artículo con
  // descripción. El bulto NO bloquea: se asigna en producción y "Sin bulto"
  // queda como chip informativo.
  const paquetesTotal = grupos.length;
  const sinCliente = (grupos || []).reduce((n, g) => n + (g.customer_id ? 0 : 1), 0);
  const sinTipo = (grupos || []).reduce((n, g) => n + (g.tipo_ingreso ? 0 : 1), 0);
  const sinItems = (grupos || []).reduce(
    (n, g) =>
      n +
      ((g.items || []).some((it) => String(it.descripcion ?? it.description ?? it.name ?? '').trim())
        ? 0
        : 1),
    0
  );
  const listo = paquetesTotal > 0 && sinCliente === 0 && sinTipo === 0 && sinItems === 0;
  // Traza de la compuerta: útil para explicar por qué Confirmar está apagado,
  // pero corre en cada render, así que no viaja al bundle de producción.
  if (process.env.NODE_ENV !== 'production') {
    console.log(
      `[ExtractionFooter] gate: paquetes=${paquetesTotal} sinCliente=${sinCliente} sinTipo=${sinTipo} sinItems=${sinItems} enabled=${listo}`
    );
  }

  let motivoBloqueo = '';
  if (paquetesTotal === 0) motivoBloqueo = 'No hay paquetes para agregar';
  else if (sinCliente > 0) motivoBloqueo = `Falta asignar cliente en ${sinCliente} paquete(s)`;
  else if (sinTipo > 0) motivoBloqueo = `Falta tipo de ingreso en ${sinTipo} paquete(s)`;
  else if (sinItems > 0) motivoBloqueo = `Falta un artículo con descripción en ${sinItems} paquete(s)`;

  // ── Totales del pie ─────────────────────────────────────────────────────
  // El idioma de los campos varía según de dónde venga el ítem: cada lectura
  // lleva su cadena de respaldo bilingüe.
  const valorArticulo = (it) =>
    (Number(it.cantidad ?? it.quantity ?? 1) || 1) *
    (Number(it.cip_unitario_usd ?? it.fob_unitario_usd ?? it.unit_price ?? it.valor_unitario ?? 0) || 0);
  const totalPaquete = (p) => (p.items || []).reduce((s, it) => s + valorArticulo(it), 0);
  const totalArticulos = (grupos || []).reduce((n, g) => n + (g.items || []).length, 0);
  const valorDeclarado = (grupos || []).reduce((s, g) => s + totalPaquete(g), 0);

  // Atajo "Marcar todo revisado": apaga requires_review en todas las filas de
  // todos los grupos pasando por cambiarItem (nunca escribe estado directo).
  // Solo toca requires_review — el vínculo de catálogo, precio y descripción
  // son ortogonales: una fila manual a $0 también puede quedar revisada.
  const pendientesRevision = (grupos || []).reduce(
    (n, g) => n + (g.items || []).filter((it) => it.requires_review).length,
    0
  );
  const marcarTodoRevisado = () => {
    (grupos || []).forEach((g) =>
      (g.items || []).forEach((it) => {
        if (it.requires_review) cambiarItem(g.key, it.key, { requires_review: false });
      })
    );
  };
  const paquetesSobreUmbral = (grupos || []).reduce((n, g) => {
    const umbral = UMBRAL_DGA[g.tipo_ingreso];
    return n + (umbral !== undefined && totalPaquete(g) > umbral ? 1 : 0);
  }, 0);

  const notas = borrador?.extraction_notes ?? resultado?.extracted?.extraction_notes;
  const esCached = Boolean(cached || borrador?.cached);
  const metaRef = useRef({});
  metaRef.current = { notas, esCached };

  // Foto del estado para guardar como borrador (misma forma que `grupos`).
  const fotoBorrador = () => ({
    version: VERSION_BORRADOR,
    grupos: clonar(gruposRef.current),
    extraction_notes: metaRef.current.notas ?? '',
    cached: metaRef.current.esCached,
  });

  // Esc / ✕ / fondo: cerrar sin confirmar ni descartar ⇒ se guarda el borrador.
  const cerrarGuardando = useCallback(() => {
    const { onClose: cerrar, onGuardarBorrador: guardar, pendiente: ocupado } = cierreRef.current;
    if (ocupado) return;
    if (guardar && gruposRef.current.length > 0) guardar(fotoBorrador());
    cerrar && cerrar();
  }, []);

  const descartar = () => {
    if (pendiente) return;
    onDescartar && onDescartar();
    onClose && onClose();
  };

  const confirmar = async () => {
    if (!listo || pendiente) return;
    setPendiente(true);
    setErrorConfirmar('');
    const payload = grupos.map((g) => ({
      customer_id: g.customer_id,
      nombre: g.nombre,
      bulto_id: g.bulto_id ?? null,
      tipo_ingreso: g.tipo_ingreso ?? null,
      items: g.items.map((it) => ({
        descripcion: it.descripcion,
        cantidad: Number(it.cantidad) || 1,
        unidad_medida: it.unidad_medida || 'UNIDAD',
        fob_unitario_usd: Number(it.fob_unitario_usd) || 0,
        is_new: Boolean(it.is_new),
        requires_review: Boolean(it.requires_review),
        // Aditivos: solo viajan cuando aportan algo (la forma base no cambia).
        ...(it.customs_catalog_id
          ? { customs_catalog_id: it.customs_catalog_id, cip_unitario_usd: it.cip_unitario_usd ?? null }
          : {}),
        ...(it.extraction_source === 'manual' ? { extraction_source: 'manual' } : {}),
      })),
    }));
    try {
      await onConfirmar(payload);
      setPendiente(false);
      onClose && onClose();
    } catch (err) {
      setPendiente(false);
      setErrorConfirmar(err?.message || 'No se pudieron agregar los paquetes. Intente de nuevo.');
    }
  };

  const footer = (
    <div className="flex flex-col gap-2">
      {errorConfirmar && (
        <p role="alert" className="amb-alert">
          {errorConfirmar}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* Totales: una sola línea a ancho de escritorio; bajo ~900px el
            flex-wrap los baja a una segunda línea sobre los botones. */}
        <div className="min-w-0">
          <p className="amb-total">
            <span className="amb-num">{paquetesTotal} paquetes</span>
            <span>{totalArticulos} artículos</span>
            <button
              type="button"
              onClick={marcarTodoRevisado}
              disabled={pendiente || pendientesRevision === 0}
              title={
                pendientesRevision > 0
                  ? 'Marca todos los artículos pendientes como revisados'
                  : 'No hay artículos pendientes de revisión'
              }
              className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
            >
              {pendientesRevision > 0
                ? `Marcar todo revisado (${pendientesRevision})`
                : 'Marcar todo revisado'}
            </button>
            <span>
              Valor declarado <span className="amb-num">${valorDeclarado.toFixed(2)}</span>
            </span>
          </p>
          {paquetesSobreUmbral > 0 && (
            <p className="amb-warn mt-0.5">{paquetesSobreUmbral} paquete(s) sobre el umbral</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={descartar}
            disabled={pendiente}
            className="amb-btn amb-btn--secondary"
          >
            Descartar
          </button>
          {/* Excepción: el relleno fuerte del primario NO cambia (bg-slate-900 /
              hover bg-slate-800). Ambiance le aporta radio, tipografía y sombra
              vía .amb-btn--primary, que a propósito no declara `background`. */}
          <button
            type="button"
            onClick={confirmar}
            disabled={!listo || pendiente}
            title={listo ? undefined : motivoBloqueo || undefined}
            className="amb-btn amb-btn--primary bg-slate-900 hover:bg-slate-800"
          >
            {pendiente && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {pendiente ? 'Agregando…' : 'Confirmar y agregar'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Drawer
      open={open}
      title="Revisar lista extraída"
      onClose={cerrarGuardando}
      width="xl"
      footer={footer}
      ambiance
    >
      {avisoVisible && (
        <div className="amb-banner mb-3" role="status">
          <AlertTriangle size={14} className="amb-banner-icon mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="amb-banner-title">Lo extraído es un borrador: revisá cada fila antes de confirmar.</p>
            {notas && <p className="amb-banner-body">{notas}</p>}
          </div>
          {esCached && (
            <span
              className="amb-chip amb-chip--neutral shrink-0"
              title="Esta foto ya se había analizado: se reutilizó el resultado guardado"
            >
              Resultado guardado — sin costo de análisis
            </span>
          )}
          <button
            type="button"
            onClick={() => setAvisoVisible(false)}
            aria-label="Descartar aviso"
            title="Descartar aviso"
            className="amb-banner-close"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {cargando && (
        <p role="status" className="amb-status mb-3">
          <Loader2 size={13} className="animate-spin" aria-hidden="true" /> Analizando la lista…
        </p>
      )}
      {error && (
        <p role="alert" className="amb-alert mb-3">
          {error}
        </p>
      )}

      {/* Móvil: visor colapsable arriba, lista a lo ancho debajo. Escritorio
          (md:): división lado a lado, visor sticky a la izquierda. */}
      <div className="flex min-h-0 flex-col gap-3 md:flex-row md:items-start md:gap-4">
        <SourcePane tipo={sourceTipo} imageUrl={imageUrl} sourceMeta={sourceMeta} />

        {/* Grupos */}
        <section aria-label="Paquetes extraídos" className="flex min-w-0 flex-1 flex-col gap-3">
          {grupos.length === 0 && !cargando && (
            <p className="amb-empty">No se detectaron paquetes en la lista.</p>
          )}
          {grupos.map((g, gi) => (
            <article key={g.key} aria-label={`Grupo ${g.owner_name_raw || gi + 1}`} className="amb-card">
              <header className="amb-card-head">
                {editandoNombre === g.key ? (
                  <input
                    type="text"
                    autoFocus
                    defaultValue={g.owner_name_raw}
                    aria-label="Nombre del cliente leído"
                    placeholder="Nombre del cliente"
                    disabled={pendiente}
                    onBlur={(e) => confirmarNombre(g, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        confirmarNombre(g, e.currentTarget.value);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditandoNombre(null);
                      }
                    }}
                    className="amb-in amb-in--title min-w-0 flex-1"
                  />
                ) : (
                  <>
                    <h3 className="amb-card-title min-w-0 flex-1 truncate">
                      {g.owner_name_raw || 'Sin nombre'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setEditandoNombre(g.key)}
                      disabled={pendiente}
                      aria-label="Editar nombre del cliente"
                      title="Corregir el nombre leído antes de buscar el cliente"
                      className="amb-ibtn amb-ibtn--bare"
                    >
                      <Pencil size={13} aria-hidden="true" />
                    </button>
                  </>
                )}
                <ProvenanceChip source={g.extraction_source === 'manual' ? 'manual' : 'ai'} ambiance />
                {g.extraction_source === 'manual' && (
                  <button
                    type="button"
                    onClick={() => quitarGrupo(g.key)}
                    disabled={pendiente}
                    aria-label="Quitar cliente agregado"
                    title="Quitar cliente agregado"
                    className="amb-ibtn amb-ibtn--bare amb-ibtn--danger"
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                )}
              </header>
              <div className="amb-card-fields">
                <ClientePicker
                  id={`ext-cliente-${g.key}`}
                  value={g.customer_id}
                  customers={candidatosPorGrupo[gi] || customers}
                  onChange={(id, c) =>
                    // Tocar el selector es decisión del operador: el flag de
                    // auto-asignación se apaga aunque elija el mismo cliente.
                    cambiarGrupo(g.key, {
                      customer_id: id,
                      nombre: c?.full_name ?? g.owner_name_raw,
                      customer_auto: false,
                    })
                  }
                  onQuickCreate={onQuickCreate}
                  disabled={pendiente}
                  ambiance
                />
                {g.customer_auto && g.customer_id && <AutoAsignadoChip />}
                <BultoSelect
                  bultos={bultos}
                  value={g.bulto_id}
                  onChange={(bulto_id) => cambiarGrupo(g.key, { bulto_id })}
                  disabled={pendiente}
                  ambiance
                />
                <TipoIngresoControl
                  compact
                  value={g.tipo_ingreso}
                  onChange={(tipo_ingreso) => cambiarGrupo(g.key, { tipo_ingreso })}
                  disabled={pendiente}
                  ambiance
                />
              </div>
              <div className="amb-card-items">
                {g.items.length === 0 ? (
                  <p className="amb-warn py-3 text-center">
                    Este grupo quedó sin artículos: agregalo a mano o descartalo.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {g.items.map((it) => {
                      const ligado = Boolean(it.customs_catalog_id);
                      const hoverEnEstaFila =
                        hoveredCandidateId !== null &&
                        hoveredCandidateId.itemKey === it.key;
                      const previa = hoverEnEstaFila ? hoveredCandidateId : null;
                      // Marca de 2 px en el borde: azul = por revisar, teal = fila
                      // resuelta, neutra = todavía vacía (igual que el diseño).
                      const marca = it.requires_review
                        ? ' amb-item--review'
                        : String(it.descripcion || '').trim()
                        ? ' amb-item--ok'
                        : '';
                      return (
                        <li
                          key={it.key}
                          aria-label={`Artículo extraído ${it.descripcion || 'sin descripción'}`}
                          className={`amb-item${marca}`}
                        >
                          {/* Ligado: se muestra la descripción del catálogo en el teal del
                              chip "Catálogo ✓", pero `descripcion` (el texto crudo
                              extraído) queda intacto debajo. */}
                          <input
                            type="text"
                            value={ligado ? it.catalogo_descripcion ?? it.descripcion : it.descripcion}
                            maxLength={MAX_DESC}
                            aria-label="Descripción"
                            placeholder="Descripción del artículo"
                            readOnly={ligado}
                            onChange={(e) =>
                              cambiarItem(g.key, it.key, { descripcion: e.target.value.slice(0, MAX_DESC) })
                            }
                            disabled={pendiente}
                            className={`${CAMPO_FILA} amb-in--body w-full${ligado ? ' amb-in--cat' : ''}`}
                          />
                          <CatalogSugerencias
                            descripcion={it.descripcion}
                            ligado={
                              it.customs_catalog_id
                                ? { id: it.customs_catalog_id, descripcion: it.catalogo_descripcion }
                                : null
                            }
                            onElegir={(row) => elegirCatalogo(g, it, row)}
                            onDesligar={() => desligarCatalogo(g, it)}
                            onHoverCandidato={(row) =>
                              setHoveredCandidateId(
                                row ? { itemKey: it.key, id: row.id, cip_usd: row.cip_usd } : null
                              )
                            }
                            disabled={pendiente}
                          />
                          {/* La columna de la fila mide ~440px (drawer xl − SourcePane −
                              paddings): anchos y gaps están calibrados para que las tres
                              unidades + borrar quepan en una sola línea; flex-wrap queda
                              solo como red para viewports angostos. */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-2">
                            {/* Cada número lleva su rótulo a la derecha: tres campos
                                numéricos apilados sin etiqueta son indistinguibles. */}
                            <label className="amb-field--row" htmlFor={`ext-cant-${it.key}`}>
                              <input
                                id={`ext-cant-${it.key}`}
                                type="number"
                                min={1}
                                step={1}
                                value={it.cantidad}
                                onChange={(e) => cambiarItem(g.key, it.key, { cantidad: e.target.value })}
                                disabled={pendiente}
                                className={`${CAMPO_FILA} amb-in--mono amb-in--r w-14`}
                              />
                              <span className="amb-label">{etiquetaCantidad(it.unidad_medida)}</span>
                            </label>
                            <label className="amb-field--row" htmlFor={`ext-unidad-${it.key}`}>
                              <select
                                id={`ext-unidad-${it.key}`}
                                value={it.unidad_medida}
                                onChange={(e) => cambiarItem(g.key, it.key, { unidad_medida: e.target.value })}
                                disabled={pendiente}
                                className={`${CAMPO_FILA} amb-sel amb-in--muted w-20`}
                              >
                                {UNIDADES.map((u) => (
                                  <option key={u} value={u}>
                                    {u}
                                  </option>
                                ))}
                              </select>
                              <span className="amb-label">Unidad</span>
                            </label>
                            {/* Con vista previa activa el input muestra el CIP del candidato
                                en cursiva apagada, solo lectura: nunca escribe en el ítem. */}
                            <label className="amb-field--row" htmlFor={`ext-fob-${it.key}`}>
                              <input
                                id={`ext-fob-${it.key}`}
                                type="number"
                                min={0}
                                step={0.01}
                                value={previa ? Number(previa.cip_usd ?? 0).toFixed(2) : it.fob_unitario_usd}
                                readOnly={Boolean(previa)}
                                onChange={(e) =>
                                  cambiarItem(g.key, it.key, {
                                    fob_unitario_usd: e.target.value,
                                    sin_precio: false,
                                  })
                                }
                                disabled={pendiente}
                                className={`${CAMPO_FILA} amb-in--mono amb-in--r w-16${
                                  previa ? ' amb-in--previa' : it.sin_precio ? ' amb-in--sinprecio' : ''
                                }`}
                              />
                              <span className="amb-label amb-label--wrap">Valor unitario (FOB)</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => quitarItem(g.key, it.key)}
                              aria-label="Quitar artículo extraído"
                              title="Quitar artículo extraído"
                              disabled={pendiente}
                              className="amb-ibtn amb-ibtn--bare amb-ibtn--danger"
                            >
                              <Trash2 size={13} aria-hidden="true" />
                            </button>
                          </div>
                          {ligado && it.catalogo_unidad_medida === 'KILOGRAMO' && it.unidad_medida === 'LB' && (
                            <p className="amb-warn mt-1">
                              Precio del catálogo es por kilogramo — confirmá la unidad.
                            </p>
                          )}
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <IsNewToggle
                              value={it.is_new}
                              onChange={(is_new) => cambiarItem(g.key, it.key, { is_new })}
                              disabled={pendiente}
                              ambiance
                            />
                            <ProvenanceChip
                              source={it.extraction_source === 'manual' ? 'manual' : 'ai'}
                              ambiance
                            />
                            <ReviewDot visible={it.requires_review} ambiance />
                            {it.sin_precio && <span className="amb-warn">Sin precio en la lista</span>}
                            {it.notes && <span className="amb-note">{it.notes}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => agregarItem(g.key)}
                  disabled={pendiente}
                  className="amb-btn--dash mt-2"
                >
                  <Plus size={12} aria-hidden="true" /> Agregar artículo
                </button>
              </div>
            </article>
          ))}
          <button
            type="button"
            onClick={agregarGrupo}
            disabled={pendiente || cargando}
            className="amb-btn--dash amb-btn--dash-lg self-start"
          >
            <Plus size={14} aria-hidden="true" /> Agregar cliente
          </button>
        </section>
      </div>
    </Drawer>
  );
};

export default ExtractionDrawer;
